import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { type ForgotPasswordInput, forgotPasswordSchema } from '@brimostech/shared';
import { Button, Input, Label, Spinner } from '@brimostech/ui';
import { AuthLayout } from '@/features/auth/login-page';
import { api } from '@/lib/api-client';
import { errorMessage } from '@/lib/api-errors';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { identifier: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await api('/auth/forgot-password', { method: 'POST', body: values, auth: false });
      setSent(values.identifier);
    } catch (error) {
      setServerError(errorMessage(error));
    }
  });

  if (sent) {
    return (
      <AuthLayout>
        <div className="space-y-4">
          <span className="flex size-12 items-center justify-center rounded-full bg-success/10 text-success">
            <MailCheck className="size-6" />
          </span>
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Verifique suas mensagens</h1>
            <p className="text-sm text-muted-foreground">
              Se existir uma conta para <strong>{sent}</strong>, enviamos um link e um código de 6
              dígitos para redefinir a senha. Eles valem por 30 minutos.
            </p>
          </div>
          <Button asChild className="w-full">
            <Link to={`/redefinir-senha?identifier=${encodeURIComponent(sent)}`}>
              Já tenho o código
            </Link>
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">Voltar para o login</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Esqueci minha senha</h1>
        <p className="text-sm text-muted-foreground">
          Informe o e-mail ou celular da sua conta. Enviaremos um link e um código para criar uma
          nova senha.
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="identifier">E-mail ou celular</Label>
          <Input
            id="identifier"
            autoFocus
            autoComplete="username"
            placeholder="voce@exemplo.com.br"
            aria-invalid={!!form.formState.errors.identifier}
            {...form.register('identifier')}
          />
          {form.formState.errors.identifier ? (
            <p className="text-sm text-destructive">{form.formState.errors.identifier.message}</p>
          ) : null}
        </div>
        {serverError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {serverError}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? <Spinner /> : null}
          Enviar instruções
        </Button>
        <Button asChild variant="ghost" className="w-full">
          <Link to="/login">Voltar para o login</Link>
        </Button>
      </form>
    </AuthLayout>
  );
}
